/**
 * Intel360ServiceCard — CANONICAL Inteligência 360 service card.
 *
 * Golden master: layout dos 3 cards de lançamento (Compliance 360, CNPJ 360,
 * Processo Timeline). Todos os demais serviços das Fases 1 e 3 devem herdar
 * a MESMA geometria (altura, padding, espaçamentos, hierarquia, posição do CTA).
 *
 * Regras (Seção 11-13, 21-31):
 *  - A cor temática do CTA muda por categoria; nada mais muda.
 *  - Contextual pricing por entitlement: incluído / franquia esgotada /
 *    sob demanda / always_paid / unlimited.
 *  - Frontend NÃO decide preço — recebe `price_cents` do backend.
 *  - Sem exposição de nomenclatura interna (Fase 1/3/5) ao cliente final.
 */
import { Zap, ChevronRight, CheckCircle2, Timer, ShieldCheck } from "lucide-react";
import { getLucideIcon } from "@/lib/lucideIcon";

const NAVY = "#0B1B2F";
const GOLD = "#C3A05A";

const BADGE_STYLE = {
  novo:        { bg: "bg-emerald-600", label: "NOVO" },
  new:         { bg: "bg-emerald-600", label: "NOVO" },
  popular:     { bg: "bg-rose-600",    label: "POPULAR" },
  recomendado: { bg: "bg-[#C3A05A]",   label: "RECOMENDADO" },
  destaque:    { bg: "bg-[#C3A05A]",   label: "DESTAQUE" },
  "em breve":  { bg: "bg-slate-500",   label: "EM BREVE" },
  breve:       { bg: "bg-slate-500",   label: "EM BREVE" },
  beta:        { bg: "bg-indigo-600",  label: "BETA" },
};

function normalizeBadge(raw) {
  if (!raw || typeof raw !== "string") return null;
  const k = raw.trim().toLowerCase();
  return BADGE_STYLE[k] || { bg: "bg-slate-700", label: raw.toUpperCase().slice(0, 12) };
}

/**
 * Tema por categoria — o resto do card permanece idêntico.
 * Todas as combinações são premium/sóbrias, alinhadas à identidade Hermes.
 */
export const INTEL_SECTION_THEME = {
  compliance:   { cta: "bg-emerald-700 hover:bg-emerald-800 focus:ring-emerald-500", accent: "text-emerald-700", ring: "border-emerald-300" },
  empresarial:  { cta: "bg-sky-700 hover:bg-sky-800 focus:ring-sky-500",             accent: "text-sky-700",     ring: "border-sky-300" },
  judicial:     { cta: "bg-slate-800 hover:bg-slate-900 focus:ring-slate-500",       accent: "text-slate-700",   ring: "border-slate-300" },
  pessoa:       { cta: "bg-amber-700 hover:bg-amber-800 focus:ring-amber-500",       accent: "text-amber-700",   ring: "border-amber-300" },
  dossie:       { cta: "bg-purple-700 hover:bg-purple-800 focus:ring-purple-500",    accent: "text-purple-700",  ring: "border-purple-300" },
  fiscal:       { cta: "bg-rose-700 hover:bg-rose-800 focus:ring-rose-500",          accent: "text-rose-700",    ring: "border-rose-300" },
  contratacoes: { cta: "bg-sky-700 hover:bg-sky-800 focus:ring-sky-500",             accent: "text-sky-700",     ring: "border-sky-300" },
  legislativo: { cta: "bg-indigo-700 hover:bg-indigo-800 focus:ring-indigo-500",     accent: "text-indigo-700",  ring: "border-indigo-300" },
  minerario:    { cta: "bg-amber-800 hover:bg-amber-900 focus:ring-amber-600",       accent: "text-amber-800",   ring: "border-amber-300" },
  ambiental:    { cta: "bg-emerald-800 hover:bg-emerald-900 focus:ring-emerald-600", accent: "text-emerald-800", ring: "border-emerald-300" },
  rural:        { cta: "bg-lime-700 hover:bg-lime-800 focus:ring-lime-500",          accent: "text-lime-700",    ring: "border-lime-300" },
  watch:        { cta: "bg-orange-700 hover:bg-orange-800 focus:ring-orange-500",    accent: "text-orange-700",  ring: "border-orange-300" },
  calculos:     { cta: "bg-cyan-700 hover:bg-cyan-800 focus:ring-cyan-500",          accent: "text-cyan-700",    ring: "border-cyan-300" },
  mercado:      { cta: "bg-slate-800 hover:bg-slate-900 focus:ring-slate-500",       accent: "text-slate-700",   ring: "border-slate-300" },
  obras:        { cta: "bg-amber-700 hover:bg-amber-800 focus:ring-amber-500",       accent: "text-amber-700",   ring: "border-amber-300" },
  adicional:    { cta: "bg-slate-700 hover:bg-slate-800 focus:ring-slate-500",       accent: "text-slate-700",   ring: "border-slate-300" },
};

// Nomes humanos das fontes oficiais catalogadas
const SOURCE_LABELS = {
  rfb_cnpj:            "Receita Federal (BrasilAPI)",
  cgu_transparencia:   "CGU · Portal da Transparência",
  pgfn_divida_ativa:   "PGFN · Dívida Ativa",
  tcu:                 "TCU",
  pncp:                "PNCP · Contratações Públicas",
  cnj_datajud:         "CNJ · DataJud",
  cnj_djen:            "CNJ · DJEN",
  ibama_autos:         "IBAMA · Autos",
  ibama_embargos:      "IBAMA · Embargos",
  camara:              "Câmara dos Deputados",
  senado:              "Senado Federal",
  anm_scm:             "ANM · SCM",
  anm_sigmine:         "ANM · SIGMINE",
  anm_cfem:            "ANM · CFEM",
  cafir:               "RFB · CAFIR",
  sncr:                "INCRA · SNCR",
  incra_sigef:         "INCRA · SIGEF",
  cno_obras:           "RFB · CNO Obras",
  cvm:                 "CVM · Mercado",
  bcb:                 "Banco Central do Brasil",
  inlabs_dou:          "INLABS · Diário Oficial da União",
};
export function labelForSource(slug) { return SOURCE_LABELS[slug] || slug; }

/**
 * Gera 3 bullets deterministicamente a partir do produto quando não houver
 * bullets curados (LAUNCH_META). NUNCA inventa dado que não venha do backend.
 */
export function buildBullets(product) {
  // 1) Se o backend enviou full_description com múltiplas linhas, use como bullets.
  const fromDesc = String(product?.full_description || "")
    .split("\n").map(s => s.trim()).filter(Boolean).slice(0, 3);
  if (fromDesc.length >= 2) return fromDesc.concat(new Array(3 - fromDesc.length).fill(null)).filter(Boolean);

  // 2) Fallback determinístico a partir de source_slugs (mantém compat)
  const sources = product?.source_slugs || [];
  const primary = sources[0] ? `Fonte oficial: ${labelForSource(sources[0])}` : "Fonte oficial: consulta pública catalogada";
  const secondary = sources.length > 1
    ? `Cruzamento com ${sources.slice(1).map(labelForSource).join(" · ")}`
    : `Retorno auditado com registro e carimbo de origem`;
  const tertiary = product?.active
    ? "Sem invenção · exibe status FOUND / NOT_FOUND / SOURCE_UNAVAILABLE"
    : "Integração em preparação — liberada após homologação da fonte";
  // Se veio uma única linha em full_description, ela vira o primary
  if (fromDesc.length === 1) return [fromDesc[0], secondary, tertiary];
  return [primary, secondary, tertiary];
}

/**
 * Formata BRL. Nunca aceita fallback numérico "solto" — recebe centavos.
 */
export function formatBRL(cents) {
  return `R$ ${(Number(cents || 0) / 100).toFixed(2).replace(".", ",")}`;
}

/**
 * Componente CANÔNICO. Todos os cards da Inteligência 360 (lançamento + catálogo)
 * usam este componente. Somente conteúdo/tema mudam.
 */
export default function Intel360ServiceCard({
  product,          // { slug, name, category, icon, short_description, full_description, badge, current_price_cents, unit_label, active, source_slugs, billing_mode, status }
  meta,             // [DEPRECATED] mantido para compat — se ausente, tudo vem do backend
  entitlement,      // opcional — resposta de /billing/entitlements/{code}
  onBuy,            // handler consultar
  categoryLabel,    // rótulo humano da categoria (ex.: "Compliance")
}) {
  const theme = INTEL_SECTION_THEME[product.category] || INTEL_SECTION_THEME.adicional;
  // Ícone: 1º meta.icon (compat), 2º product.icon (string do backend), 3º ShieldCheck
  const Icon = meta?.icon || getLucideIcon(product?.icon, ShieldCheck);
  const title = meta?.label || product.name;
  const tagline = meta?.tagline || product.short_description || product.name;
  const bullets = meta?.bullets || buildBullets(product);
  const badge = normalizeBadge(product?.badge);

  const active = !!product.active && (product.status || "ACTIVE").toUpperCase() === "ACTIVE";

  // Contextual pricing (Seções 21-27)
  const ent = entitlement || null;
  const alwaysPaid = !!(ent?.always_paid);
  const unlimited = !!(ent?.unlimited);
  const included = Number(ent?.included ?? 0);
  const remaining = Number(ent?.remaining ?? 0);
  const franquiaDisponivel = active && ent && (unlimited || (included > 0 && remaining > 0));
  const franquiaEsgotada = active && ent && !unlimited && included > 0 && remaining === 0;
  const semFranquia = active && ent && !unlimited && included === 0 && !alwaysPaid;
  // Sem entitlement mapeado no billing: cai no modo "sob demanda" (mostra preço)
  const noEntitlementInfo = active && !ent;
  const shouldShowPrice = !unlimited && (alwaysPaid || franquiaEsgotada || semFranquia || noEntitlementInfo || !active);

  // Rótulo do bloco comercial (canto direito)
  let billingLabel = "Por consulta";
  if (unlimited) billingLabel = "Uso ilimitado";
  else if (franquiaDisponivel) billingLabel = "Incluído no plano";
  else if (franquiaEsgotada) billingLabel = "Franquia utilizada";
  else if (semFranquia || noEntitlementInfo) billingLabel = "Por consulta";
  if (alwaysPaid) billingLabel = "Por consulta";

  // Rótulo do CTA
  let ctaLabel;
  if (!active) ctaLabel = "Indisponível temporariamente";
  else if (unlimited || franquiaDisponivel) ctaLabel = "Consultar";
  else ctaLabel = `Consultar por ${formatBRL(product.current_price_cents)}`;

  return (
    <div
      data-testid={`intel-card-${product.slug}`}
      data-category={product.category}
      className={`relative h-full flex flex-col rounded-2xl border bg-white p-5 shadow-sm transition
                  ${active ? "border-slate-200 hover:shadow-md hover:border-slate-300" : "border-slate-200 opacity-95"}`}
    >
      {badge && (
        <div
          data-testid={`intel-badge-${product.slug}`}
          className={`absolute -top-2 left-4 text-[9.5px] tracking-widest font-black text-white px-2 py-0.5 rounded ${badge.bg}`}
        >
          {badge.label}
        </div>
      )}
      {/* HEADER: ícone + categoria/título + billingArea */}
      <div className="flex items-start gap-3">
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: `${GOLD}18`, border: `1px solid ${GOLD}55` }}
        >
          <Icon className="w-5 h-5" style={{ color: GOLD }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className={`text-[10px] uppercase tracking-widest font-semibold ${theme.accent}`}>
            {categoryLabel || product.category}
          </div>
          <div className="font-semibold text-lg leading-tight" style={{ color: NAVY }}>
            {title}
          </div>
          <div className="text-sm text-slate-600 mt-0.5 line-clamp-2">
            {tagline}
          </div>
        </div>
        {/* Billing area — altura reservada, evita layout shift */}
        <div className="text-right shrink-0 min-w-[92px] min-h-[52px] flex flex-col items-end justify-start">
          <div className="text-[10px] uppercase tracking-widest text-slate-400">
            {billingLabel}
          </div>
          {shouldShowPrice ? (
            <div
              className="text-2xl font-heading font-semibold leading-none mt-0.5"
              style={{ color: NAVY }}
              data-testid={`intel-price-${product.slug}`}
            >
              {formatBRL(product.current_price_cents)}
            </div>
          ) : franquiaDisponivel && !unlimited ? (
            <div className="text-[12.5px] text-slate-700 mt-1" data-testid={`intel-remaining-${product.slug}`}>
              <b>{remaining}</b> de {included} restantes
            </div>
          ) : unlimited ? (
            <div className="text-[12px] text-emerald-800 font-semibold mt-1">
              Ilimitado
            </div>
          ) : (
            <div className="text-[12px] text-slate-400 mt-1">—</div>
          )}
        </div>
      </div>

      {/* BENEFITS — sempre 3 bullets · flex-grow para empurrar CTA pro fundo */}
      <ul className="mt-4 text-sm text-slate-700 space-y-1.5 flex-1">
        {bullets.slice(0, 3).map((b, i) => (
          <li key={i} className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" style={{ color: GOLD }} />
            <span className="line-clamp-2">{b}</span>
          </li>
        ))}
      </ul>

      {/* CTA — mesma linha em todos os cards da grade */}
      <div className="mt-5">
        {active ? (
          <button
            onClick={onBuy}
            data-testid={`intel-buy-${product.slug}`}
            className={`w-full py-2.5 rounded-lg text-white text-sm font-semibold
                        flex items-center justify-center gap-2 focus:outline-none
                        focus:ring-2 focus:ring-offset-1 active:brightness-95
                        disabled:opacity-60 transition-colors ${theme.cta}`}
          >
            <Zap className="w-4 h-4" />
            {ctaLabel}
            <ChevronRight className="w-3 h-3 opacity-70" />
          </button>
        ) : (
          <button
            disabled
            title="Integração em preparação — aguardando homologação da fonte oficial"
            data-testid={`intel-buy-${product.slug}`}
            className="w-full py-2.5 rounded-lg text-[13.5px] font-semibold border border-slate-300
                       bg-white text-slate-500 cursor-not-allowed inline-flex items-center
                       justify-center gap-2"
          >
            <Timer className="w-4 h-4" />
            Indisponível temporariamente
          </button>
        )}
      </div>
    </div>
  );
}
